import React, { useEffect, useRef, useState } from 'react';
import { useLocalParticipant, useRoomContext } from '@livekit/components-react';
import { DisconnectReason, RoomEvent } from 'livekit-client';
import { Loader } from '@unmind/design-system';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { tracking } from 'App/Tracking';
import { Transcript } from '../Transcript';
import { ControlPanel } from '../ControlPanel';
import { HelpButton } from '../HelpButton';
import {
  LoaderContainer,
  LoadingContainer,
  RoomContainer,
  SpeakerLabel,
} from './styles';

enum NovaNotification {
  FINISHED_SPEECH = 'nova.has_finished_speech',
  CLEAR_CAPTIONS = 'nova.clear_captions',
}

type VoiceRoomProps = {
  onLeaveChat(): void;
  onError(): void;
  onHelpRequired(): void;
};

enum StartedConversationState {
  NOT_STARTED = 'NOT_STARTED',
  MUTED = 'MUTED',
  STARTED = 'STARTED',
}

type Segment = {
  text: string;
};

type TranscriptViewProps = {
  segments: Segment[];
};

type TranscriptState = Pick<TranscriptViewProps, 'segments'> & {
  novaIsSpeaking: boolean;
  shouldClearCaption: boolean;
  novaHasStartedConversation: StartedConversationState;
};

function trackLeaveEvent(reason?: DisconnectReason) {
  if (reason === DisconnectReason.CLIENT_INITIATED) {
    tracking.track('nova-voice-leave', { reason: 'user_initiated' });
  } else if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
    tracking.track('nova-voice-leave', { reason: 'inactivity' });
  } else {
    tracking.track('nova-voice-leave', { reason: 'error' });
  }
}

export const VoiceRoom = ({
  onLeaveChat,
  onError,
  onHelpRequired,
}: VoiceRoomProps) => {
  const theme = useTheme();
  const { t: translate } = useTranslation('chatbot');
  const room = useRoomContext();
  const { localParticipant, microphoneTrack } = useLocalParticipant();
  const [transcript, setTranscript] = useState<TranscriptState>({
    novaIsSpeaking: false,
    shouldClearCaption: false,
    novaHasStartedConversation: StartedConversationState.NOT_STARTED,
    segments: [],
  });
  const registeredRpcMethodRef = useRef(false);

  useEffect(() => {
    if (!room || !localParticipant.identity || registeredRpcMethodRef.current) {
      return;
    }

    room.registerRpcMethod(
      NovaNotification.FINISHED_SPEECH,
      async (): Promise<string> => {
        setTranscript(prev => ({
          ...prev,
          novaIsSpeaking: false,
        }));

        return 'OK';
      },
    );

    room.registerRpcMethod(
      NovaNotification.CLEAR_CAPTIONS,
      async (): Promise<string> => {
        setTranscript(prev => ({
          ...prev,
          shouldClearCaption: true,
        }));

        return 'OK';
      },
    );

    room.registerTextStreamHandler(
      'lk.transcription',
      async (reader, participantInfo) => {
        for await (const chunk of reader) {
          if (participantInfo.identity === localParticipant.identity) {
            setTranscript(prev => ({
              ...prev,
              shouldClearCaption: true,
            }));

            continue;
          }

          const newSeg: Segment = { text: chunk };

          setTranscript(prev => {
            const startedConversationState =
              prev.novaHasStartedConversation ===
              StartedConversationState.NOT_STARTED
                ? StartedConversationState.MUTED
                : prev.novaHasStartedConversation;

            const newSegments = prev.shouldClearCaption
              ? [newSeg]
              : [...prev.segments, newSeg];

            return {
              ...prev,
              novaIsSpeaking:
                newSegments.length === 1 ? true : prev.novaIsSpeaking,
              novaHasStartedConversation: startedConversationState,
              segments: newSegments,
              shouldClearCaption: false,
            };
          });
        }
      },
    );

    room.on(RoomEvent.Disconnected, reason => {
      trackLeaveEvent(reason);
      if (reason === DisconnectReason.CLIENT_INITIATED) {
        return;
      }

      if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
        onLeaveChat();
      } else {
        onError();
      }
    });

    registeredRpcMethodRef.current = true;
  }, [room, localParticipant.identity, onLeaveChat, onError]);

  const handleLeaveChat = async () => {
    await room.disconnect();
    onLeaveChat();
  };

  const handleHelpRequired = async () => {
    await room.disconnect();
    onHelpRequired();
  };

  useEffect(() => {
    if (
      transcript.novaHasStartedConversation !== StartedConversationState.MUTED
    ) {
      return;
    }

    setTranscript(prev => {
      void localParticipant.setMicrophoneEnabled(true);

      return {
        ...prev,
        novaHasStartedConversation: StartedConversationState.STARTED,
      };
    });
  }, [transcript.novaHasStartedConversation, localParticipant]);

  useEffect(() => {
    if (
      microphoneTrack &&
      transcript.novaHasStartedConversation ===
        StartedConversationState.NOT_STARTED
    ) {
      void localParticipant.setMicrophoneEnabled(false);
    }
  }, [
    microphoneTrack,
    transcript.novaHasStartedConversation,
    localParticipant,
  ]);

  const getSpeakerLabel = () => {
    if (!localParticipant.isMicrophoneEnabled) {
      return translate('voice.speaker_state.muted.label');
    }

    if (transcript.novaIsSpeaking) {
      return translate('voice.speaker_state.nova.label');
    }

    return translate('voice.speaker_state.user.label');
  };

  if (
    transcript.novaHasStartedConversation ===
    StartedConversationState.NOT_STARTED
  ) {
    return (
      <LoadingContainer>
        <HelpButton handleHelpRequired={handleHelpRequired} />
        <LoaderContainer>
          <Loader color={theme.colors.text.primary} />
        </LoaderContainer>
      </LoadingContainer>
    );
  }

  return (
    <RoomContainer>
      <HelpButton handleHelpRequired={handleHelpRequired} />
      <Transcript segments={transcript.segments} />
      <SpeakerLabel>{getSpeakerLabel()}</SpeakerLabel>
      <ControlPanel
        handleLeaveChat={handleLeaveChat}
        microphoneButtonDisabled={false}
        isMicrophoneEnabled={localParticipant.isMicrophoneEnabled}
        roomEstablished
        onClickMuteButton={async () => {
          await localParticipant.setMicrophoneEnabled(false);
          tracking.track('nova-voice-mute-toggle', {
            state: 'muted',
          });
        }}
        onClickUnmuteButton={async () => {
          await localParticipant.setMicrophoneEnabled(true);
          tracking.track('nova-voice-mute-toggle', {
            state: 'unmuted',
          });
        }}
      />
    </RoomContainer>
  );
};
